import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';

const isNumeric = RegExp.prototype.test.bind(/^[0-9]$/);
const isAlpha = RegExp.prototype.test.bind(/^[a-zA-Z]$/);

const SPECIAL_CHARS = {
  9: isNumeric,
  a: isAlpha,
};

/* eslint-disable no-param-reassign */

/**
 * Get a masked value
 */
export function applyMaskToValue(mask = '', value = '', {
  maskCharacter,
  selectionStart,
  selectionEnd,
  selectionDirection,
  isDeleting,
} = {}) {
  if (!mask) {
    return {
      value, selectionStart, selectionEnd, selectionDirection,
    };
  }

  let newValue = '';

  let maskI = 0;
  let i;
  for (i = 0; i < value.length; ++i) {
    const char = value[i];
    const maskChar = mask[maskI];

    if (!maskChar) {
      // done
      break;
    } else if (SPECIAL_CHARS[maskChar]) {
      if (
        maskCharacter === char ||
        SPECIAL_CHARS[maskChar](char)
      ) {
        // char matches mask
        newValue += char;
        ++maskI;
      } else {
        // invalid char, ignore
        if (selectionStart != null && selectionStart > newValue.length) { --selectionStart; }
        if (selectionEnd != null && selectionEnd > newValue.length) { --selectionEnd; }
        continue; //eslint-disable-line no-continue
      }
    } else if (char === maskChar) {
      // fixed char
      newValue += char;
      ++maskI;
    } else {
      if (maskCharacter && maskCharacter === char) {
        // ignore char
        continue; //eslint-disable-line no-continue
      }
      if (selectionStart != null && selectionStart > newValue.length) { ++selectionStart; }
      if (selectionEnd != null && selectionEnd > newValue.length) { ++selectionEnd; }
      newValue += maskChar;
      ++maskI;
      --i;
    }
  }

  if (!isDeleting) {
    if (maskCharacter) {
      while (
        maskI < mask.length
      ) {
        if (SPECIAL_CHARS[mask[maskI]]) {
          newValue += maskCharacter;
        } else {
          newValue += mask[maskI];
        }
        ++maskI;
      }
    } else {
      // predictive mask
      while (
        maskI < mask.length &&
        !SPECIAL_CHARS[mask[maskI]]
      ) {
        newValue += mask[maskI];
        ++maskI;
        if (selectionStart != null && selectionStart >= i) { ++selectionStart; }
        if (selectionEnd != null && selectionEnd >= i) { ++selectionEnd; }
      }
    }
  }

  return {
    value: newValue,
    selectionStart,
    selectionEnd,
    selectionDirection,
  };
}
/* eslint-enable no-param-reassign */

class MaskedInput extends React.Component {
  static propTypes = {
    mask: PropTypes.string,
    maskChar: PropTypes.string,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    onChange: PropTypes.func,
  };

  value = this.props.value || this.props.defaultValue || '';

  componentDidMount() {
    this.isControlled = this.props.value != null;
    this.applyMask();
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    let changed = false;
    const isControlled = nextProps.value != null;

    if (isControlled !== this.isControlled) {
      this.isControlled = isControlled;
      console.warning(new Error('Changing controlled state of masked input is not supported'));
    }

    if (
      !this.isControlled &&
      this.props.defaultValue !== nextProps.defaultValue
    ) {
      this.wrappedInstance.value = nextProps.defaultValue;
      changed = true;
    } else if (
      this.props.mask !== nextProps.mask ||
      // this.props.maskChar !== nextProps.maskChar ||
      (
        this.isControlled &&
        this.props.value !== nextProps.value
      )
    ) {
      changed = true;
    }

    if (changed) {
      this.applyMask(nextProps);
    }
  }

  @autobind onChange(...args) {
    this.applyMask();
    if (this.props.onChange) this.props.onChange(...args);
  }

  applyMask(props = this.props) {
    const prevValue = this.value;
    const value = this.isControlled ? props.value : this.wrappedInstance.value || '';
    const isDeleting = prevValue.length > value.length;
    if (isDeleting) {
      // if deleting last char don't reapply
      if (prevValue.indexOf(value) === 0) {
        this.wrappedInstance.value = value;
        this.value = value;
        return;
      }
    }
    const result = applyMaskToValue(
      props.mask,
      value,
      {
        maskCharacter: props.maskChar,
        selectionStart: this.wrappedInstance.selectionStart,
        selectionEnd: this.wrappedInstance.selectionEnd,
        selectionDirection: this.wrappedInstance.selectionDirection,
        isDeleting,
      },
    );
    if (this.wrappedInstance.value !== result.value) {
      this.wrappedInstance.value = result.value;
      setTimeout(() => this.wrappedInstance.setSelectionRange(
        result.selectionEnd,
        result.selectionEnd,
        result.selectionDirection,
      ), 0);
    }
    if (prevValue !== result.value) {
      this.value = result.value;
    }
  }

  @autobind inputRef(input) { this.wrappedInstance = input; }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      mask, maskChar, onChange, defaultValue, value, ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    return (
      <input
        {...rest}
        ref={this.inputRef}
        onChange={this.onChange}
        defaultValue={this.value}
      />
    );
  }
}

export default MaskedInput;



// WEBPACK FOOTER //
// ./spa/Components/MaskedInput.jsx